/**
 * Validation for invoices, payments, allocations, adjustments and statements —
 * Phase 11 (decisions D143–D155, D159).
 *
 * MONEY IS AN INTEGER NUMBER OF MINOR UNITS, A SAFE INTEGER, AND NOTHING ELSE.
 * A fractional amount means somebody is doing floating-point arithmetic on
 * money, and an amount beyond `Number.MAX_SAFE_INTEGER` cannot be represented
 * exactly by the client that sent it. Both are refused here.
 *
 * QUANTITIES AND PERCENTAGES ARE DECIMALS, ACCEPTED AS A NUMBER OR AS TEXT AND
 * HANDED ON AS CANONICAL TEXT — `"2.500"`, `"12.50"`, the spelling the
 * database returns for `numeric(12,3)` and `numeric(5,2)` — so the backend
 * never multiplies a float and a no-op edit compares equal.
 *
 * EVERY BODY SCHEMA IS STRICT. An unknown key is refused with a 400 rather
 * than silently dropped: a client that sends `invoiceNumber`, `status`,
 * `totalMinor`, `taxRatePercent` or `currency` learns immediately that none of
 * them can be written — the number is allocated at issuance (D143), the rate
 * and the currency are the laboratory's (D144), and every total is computed by
 * the backend from the stored inputs (D151).
 *
 * The backend re-validates every rule, and some rules only it can check: that
 * a price differs from the service's (an override, D151), that a date is not
 * after the laboratory's today, that an allocation fits a balance. Those
 * refusals carry the codes of the API documentation, not zod's.
 */
import { z } from 'zod';
import { ALL_ADJUSTMENT_KINDS, ALL_INVOICE_STATUSES, ALL_PAYMENT_METHODS, ALL_PAYMENT_RECORD_STATUSES, ALL_PAYMENT_STATUSES, MAX_INVOICE_LINES, MAX_PAYMENT_ALLOCATIONS, PERCENT_DECIMALS, QUANTITY_DECIMALS, QUANTITY_INTEGER_DIGITS, formatDecimalText, parseDecimalText, } from '@lims/shared-types';
import { currencySchema, isoDateSchema, listQuerySchema, reasonSchema, uuidSchema, } from './primitives.js';
// ---------------------------------------------------------------------------
// Shared pieces
// ---------------------------------------------------------------------------
/** An optional free-text field: absent, or actual content. Never blank. */
const optionalText = (max) => z
    .string()
    .trim()
    .max(max)
    .nullable()
    .optional()
    // An empty string is an absent value, not a value of "". Storing "" would
    // make an empty field look like a recorded fact (CLAUDE.md section 8).
    .transform((value) => (value === '' ? null : value));
/** A query flag: `'true'` / `'false'`, absent meaning "no filter". */
const optionalBooleanQuery = z
    .enum(['true', 'false'])
    .optional()
    .transform((value) => (value === undefined ? undefined : value === 'true'));
export const invoiceStatusSchema = z.enum(ALL_INVOICE_STATUSES);
export const paymentStatusSchema = z.enum(ALL_PAYMENT_STATUSES);
export const paymentRecordStatusSchema = z.enum(ALL_PAYMENT_RECORD_STATUSES);
export const paymentMethodSchema = z.enum(ALL_PAYMENT_METHODS);
export const adjustmentKindSchema = z.enum(ALL_ADJUSTMENT_KINDS);
// ---------------------------------------------------------------------------
// Money and decimals
// ---------------------------------------------------------------------------
/** An amount of money that must be MORE than zero: a payment, an allocation,
 *  an adjustment. An integer of minor units within the safe integer range. */
export const positiveMinorSchema = z
    .number()
    .int({ message: 'Amount must be an integer number of minor units' })
    .positive()
    .safe();
/** An amount that may be zero: a unit price, an amount discount. */
export const nonNegativeMinorSchema = z
    .number()
    .int({ message: 'Amount must be an integer number of minor units' })
    .nonnegative()
    .safe();
const decimalInput = z.union([z.number(), z.string().trim().max(32)]);
const QUANTITY_LIMIT = 10n ** BigInt(QUANTITY_INTEGER_DIGITS + QUANTITY_DECIMALS);
const HUNDRED_PERCENT = 100n * 10n ** BigInt(PERCENT_DECIMALS);
/**
 * A billed quantity: more than zero, at most three decimals, at most nine
 * integer digits (`numeric(12,3)`). A number or decimal text in; canonical
 * text out (`2.5` → `"2.500"`).
 */
export const quantitySchema = decimalInput.transform((value, ctx) => {
    let scaled;
    try {
        scaled = parseDecimalText(value, QUANTITY_DECIMALS);
    }
    catch {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'A quantity is a decimal with at most three decimal places',
        });
        return z.NEVER;
    }
    if (scaled <= 0n) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'A quantity must be greater than zero' });
        return z.NEVER;
    }
    if (scaled >= QUANTITY_LIMIT) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'The quantity is too large' });
        return z.NEVER;
    }
    return formatDecimalText(scaled, QUANTITY_DECIMALS);
});
/**
 * A discount percentage: 0–100, at most two decimals (`numeric(5,2)`). A
 * number or decimal text in; canonical text out (`12.5` → `"12.50"`).
 */
export const percentSchema = decimalInput.transform((value, ctx) => {
    let scaled;
    try {
        scaled = parseDecimalText(value, PERCENT_DECIMALS);
    }
    catch {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'A percentage is a decimal with at most two decimal places',
        });
        return z.NEVER;
    }
    if (scaled > HUNDRED_PERCENT) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'A percentage is at most 100' });
        return z.NEVER;
    }
    return formatDecimalText(scaled, PERCENT_DECIMALS);
});
/** Neither, or exactly one, of a percentage and an amount — never both (D43). */
function atMostOne(a, b) {
    return a === null || a === undefined || b === null || b === undefined;
}
function uniqueBy(items, key) {
    return new Set(items.map(key)).size === items.length;
}
// ---------------------------------------------------------------------------
// Invoice lines and drafts (D150, D151, D152)
// ---------------------------------------------------------------------------
/**
 * One invoice line.
 *
 * WITH a service (`workOrderServiceId`), the description is snapshotted from
 * the service and the unit price DEFAULTS to the price the work order froze;
 * a different `unitPriceMinor` is an override that needs `pricing.override`
 * and `priceOverrideReason` — checked by the API, which alone knows the
 * service's price. WITHOUT a service, the line is written by hand: a
 * description in at least one language and a unit price are required.
 *
 * The line discount is a percentage OR an amount, never both. ABSENT means
 * "the backend's default" (the service's own discount, where D151 says it
 * applies); an explicit `null` for both means "no discount on this line".
 */
export const invoiceLineInputSchema = z
    .object({
    workOrderServiceId: uuidSchema.nullable().optional(),
    descriptionAr: optionalText(500),
    descriptionEn: optionalText(500),
    quantity: quantitySchema,
    unitPriceMinor: nonNegativeMinorSchema.optional(),
    priceOverrideReason: reasonSchema.optional(),
    discountPercent: percentSchema.nullable().optional(),
    discountAmountMinor: nonNegativeMinorSchema.nullable().optional(),
})
    .strict()
    .refine((line) => Boolean(line.workOrderServiceId) || Boolean(line.descriptionAr) || Boolean(line.descriptionEn), {
    message: 'A line without a service describes itself in at least one language',
    path: ['description'],
})
    .refine((line) => Boolean(line.workOrderServiceId) || line.unitPriceMinor !== undefined, {
    message: 'A line without a service states its unit price',
    path: ['unitPriceMinor'],
})
    .refine((line) => atMostOne(line.discountPercent, line.discountAmountMinor), {
    message: 'A discount is a percentage or an amount, never both',
    path: ['discountAmountMinor'],
});
export const invoiceLinesSchema = z.array(invoiceLineInputSchema).min(1).max(MAX_INVOICE_LINES);
/** The fields a draft carries besides its client. */
const invoiceDraftShape = {
    lines: invoiceLinesSchema,
    documentDiscountPercent: percentSchema.nullable().optional(),
    documentDiscountAmountMinor: nonNegativeMinorSchema.nullable().optional(),
    documentDiscountReason: optionalText(2000),
    /** Optional: absent, the due date is set AT ISSUANCE from the laboratory's
     *  payment terms (D152). Never before the issue date — checked then. */
    dueDate: isoDateSchema.nullable().optional(),
    /** Shown to the client, the portal included. */
    clientNote: optionalText(2000),
    /** NEVER shown to the client. */
    internalNote: optionalText(4000),
};
const DOCUMENT_DISCOUNT_REFINEMENT = {
    message: 'A document discount is a percentage or an amount, never both',
    path: ['documentDiscountAmountMinor'],
};
/**
 * Open a DRAFT invoice. It has no number — the number is allocated at
 * issuance (D143) — and it carries the laboratory's current tax rate and the
 * currency of the work orders it bills (D144), both set by the backend.
 */
export const createInvoiceSchema = z
    .object({
    clientId: uuidSchema,
    ...invoiceDraftShape,
})
    .strict()
    .refine((value) => atMostOne(value.documentDiscountPercent, value.documentDiscountAmountMinor), DOCUMENT_DISCOUNT_REFINEMENT);
/**
 * Edit a draft, against the revision the editor read (D125's rule, D152).
 * `lines`, when given, REPLACES the set. The client cannot change.
 */
export const updateInvoiceDraftSchema = z
    .object({
    draftRevision: z.number().int().positive(),
    lines: invoiceLinesSchema.optional(),
    documentDiscountPercent: invoiceDraftShape.documentDiscountPercent,
    documentDiscountAmountMinor: invoiceDraftShape.documentDiscountAmountMinor,
    documentDiscountReason: invoiceDraftShape.documentDiscountReason,
    dueDate: invoiceDraftShape.dueDate,
    clientNote: invoiceDraftShape.clientNote,
    internalNote: invoiceDraftShape.internalNote,
})
    .strict()
    .refine((value) => Object.keys(value).some((key) => key !== 'draftRevision'), { message: 'Nothing to change', path: ['draftRevision'] })
    .refine((value) => atMostOne(value.documentDiscountPercent, value.documentDiscountAmountMinor), DOCUMENT_DISCOUNT_REFINEMENT);
/** Issue the draft the issuer last saw (D152). */
export const issueInvoiceSchema = z
    .object({ draftRevision: z.number().int().positive() })
    .strict();
/** Discard a draft, or cancel an issued invoice. Both keep the row. */
export const invoiceReasonSchema = z.object({ reason: reasonSchema }).strict();
/** A credit or debit against an issued invoice, by `payments.adjust` (D154). */
export const recordAdjustmentSchema = z
    .object({
    kind: adjustmentKindSchema,
    amountMinor: positiveMinorSchema,
    reason: reasonSchema,
})
    .strict();
// ---------------------------------------------------------------------------
// Payments and allocations (D153)
// ---------------------------------------------------------------------------
export const allocationInputSchema = z
    .object({
    invoiceId: uuidSchema,
    amountMinor: positiveMinorSchema,
})
    .strict();
const ALLOCATIONS_UNIQUE = {
    message: 'Each invoice appears once in one set of allocations',
    path: ['allocations'],
};
/**
 * Record money received. The currency is the laboratory's own (the backend
 * sets it); a date after the laboratory's today is refused by the API
 * (`received_on_in_future`). What the allocations leave unallocated is
 * CREDIT on the client's account — allocations that exceed the payment are
 * refused by the API as `409 CONFLICT allocation_exceeds_payment`, not here.
 */
export const recordPaymentSchema = z
    .object({
    clientId: uuidSchema,
    amountMinor: positiveMinorSchema,
    receivedOn: isoDateSchema,
    method: paymentMethodSchema,
    /** The laboratory's own receipt number, as written. */
    reference: optionalText(200),
    /** Internal. Never reaches the client. */
    note: optionalText(2000),
    allocations: z.array(allocationInputSchema).max(MAX_PAYMENT_ALLOCATIONS).optional(),
})
    .strict()
    .refine((value) => uniqueBy(value.allocations ?? [], (allocation) => allocation.invoiceId), ALLOCATIONS_UNIQUE);
/** Allocate (more of) a payment's unallocated amount. */
export const allocatePaymentSchema = z
    .object({
    allocations: z.array(allocationInputSchema).min(1).max(MAX_PAYMENT_ALLOCATIONS),
})
    .strict()
    .refine((value) => uniqueBy(value.allocations, (allocation) => allocation.invoiceId), ALLOCATIONS_UNIQUE);
/** Reverse an allocation or a payment recorded in error. Nothing is deleted. */
export const reversalReasonSchema = z.object({ reason: reasonSchema }).strict();
// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------
function rangeIsOrdered(from, to) {
    return from === undefined || to === undefined || to >= from;
}
/** Work-order service lines that may be billed. `onlyRemaining` defaults to
 *  true: lines with nothing left to bill are hidden unless asked for. */
export const billableServicesQuerySchema = listQuerySchema.extend({
    clientId: uuidSchema.optional(),
    workOrderId: uuidSchema.optional(),
    projectId: uuidSchema.optional(),
    onlyRemaining: z
        .enum(['true', 'false'])
        .default('true')
        .transform((value) => value === 'true'),
});
/**
 * The invoice register. `search` matches the invoice number, with `%` and `_`
 * taken literally by the API. `paymentStatus` and `overdue` filter on DERIVED
 * values (D152).
 */
export const invoiceListQuerySchema = listQuerySchema
    .extend({
    clientId: uuidSchema.optional(),
    status: invoiceStatusSchema.optional(),
    paymentStatus: paymentStatusSchema.optional(),
    overdue: optionalBooleanQuery,
    issuedFrom: isoDateSchema.optional(),
    issuedTo: isoDateSchema.optional(),
})
    .refine((value) => rangeIsOrdered(value.issuedFrom, value.issuedTo), {
    message: 'The end of the range is on or after its start',
    path: ['issuedTo'],
});
export const paymentListQuerySchema = listQuerySchema
    .extend({
    clientId: uuidSchema.optional(),
    status: paymentRecordStatusSchema.optional(),
    receivedFrom: isoDateSchema.optional(),
    receivedTo: isoDateSchema.optional(),
})
    .refine((value) => rangeIsOrdered(value.receivedFrom, value.receivedTo), {
    message: 'The end of the range is on or after its start',
    path: ['receivedTo'],
});
/**
 * A client's statement. Every parameter is optional, and the backend names
 * each default it applied (`defaultsApplied`): the laboratory's currency, its
 * today as `to`, and one year back plus one day as `from` (D155).
 */
export const statementQuerySchema = z
    .object({
    currency: currencySchema.optional(),
    from: isoDateSchema.optional(),
    to: isoDateSchema.optional(),
})
    .refine((value) => rangeIsOrdered(value.from, value.to), {
    message: 'A statement period ends on or after the day it starts',
    path: ['to'],
});
//# sourceMappingURL=finance.js.map